import { createMatterLiensColumns } from "@/components/features/matter/liens/matter-liens-columns";
import { MatterLienContextMenu } from "@/components/features/matter/context-menus/MatterLienContextMenu";
import { DataTable, ServerErrorFallbackCard } from "@/components/ui";
import {
  getLienId,
  useLiensViewModel,
} from "@/hooks/matter-view-models/use-liens-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useDeleteMatterLien } from "@/lib/data/react-query/matters/matter-lien";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { useUser } from "@/components/UserProvider";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { Matter } from "@/lib/types";
import { getLienContactConnection } from "@/lib/utils/connections/lien-contact-connections";
import { ErrorBoundary } from "@sentry/react";
import { useLocation } from "@tanstack/react-router";

const MatterLiensTableDangerous = ({
  matter,
  tableButtons,
}: {
  matter: Matter;
  tableButtons?: React.ReactNode;
}) => {
  const { openLienModal } = useGlobalModal();
  const { hasActionPermission } = useUser();
  const deleteLien = useDeleteMatterLien();
  const liensViewModel = useLiensViewModel(matter.id);
  const location = useLocation();

  // Check if we're on the settlement page
  const isOnSettlementPage = location.pathname.includes("/settlements");

  // Get columns with conditional additional fields when on settlements page
  const columns = createMatterLiensColumns(isOnSettlementPage);
  const canDeleteLien = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLien,
    ACTION_PERMISSION_VALUES.delete,
  );

  return (
    <DataTable
      entityLabelPlural="liens"
      columns={columns}
      data={liensViewModel.map((lien) => {
        return {
          ...lien,
          matterLienHolder: getLienContactConnection(lien, "Lien Holder"),
          matterLienRepresentative: getLienContactConnection(
            lien,
            "Lien Representative",
          ),
        };
      })}
      onRowClick={(row) => {
        openLienModal({
          matterId: matter.id,
          lienId: getLienId(row.original),
          editing: true,
          initialTab: "details",
        });
      }}
      renderRowContextMenu={(row, trigger) => {
        const lien = row.original;
        const lienId = getLienId(lien);
        const label = lien.label || "lien";
        return (
          <MatterLienContextMenu
            canDelete={canDeleteLien}
            id={lienId}
            isDeleting={deleteLien.isPending}
            label={label}
            matter={matter}
            onEdit={() => {
              openLienModal({
                matterId: matter.id,
                lienId,
                editing: true,
                initialTab: "details",
              });
            }}
            onDelete={async () => {
              await deleteLien.mutateAsync({ matterId: matter.id, id: lienId });
            }}
            originalAmount={lien.originalAmount}
            trigger={trigger}
          />
        );
      }}
      tableName={tanstackTableNames.matterLiens}
      initialSort={{
        id: "Date Created",
        desc: true,
      }}
      tableButtons={tableButtons}
    />
  );
};

export const MatterLiensTable = (props: {
  matter: Matter;
  tableButtons?: React.ReactNode;
}) => (
  <ErrorBoundary
    fallback={
      <ServerErrorFallbackCard message="Unable to load liens table right now." />
    }
  >
    <MatterLiensTableDangerous {...props} />
  </ErrorBoundary>
);
